import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import debounce from 'lodash.debounce';
import { Check, Star } from "lucide-react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux'
import { Spin } from "antd";

import { setField } from "../../../slices/auth";

import { STORAGE_KEY } from '../../../constants/onBoarding'

const plans = [
  {
    id: "5",
    name: "Starter",
    price: 50,
    period: "month",
    description: "Perfect for small businesses getting started",
    features: [],
    comingSoon: true,
  },
  {
    id: "6",
    name: "Standard",
    price: 100,
    period: "month",
    description: "Ideal for growing businesses",
    features: [
      "Up to 1,000 products",
      "Advanced analytics & reports",
      "Priority support",
      "3 marketplace connections",
      "Real-time sync",
      "Inventory forecasting",
      "Custom branding"
    ],
    recommended: true,
    popular: true,
  },
  {
    id: "7",
    name: "Enterprise",
    price: 500,
    period: "month",
    description: "For large-scale operations",
    features: [],
    comingSoon: true,
  },
];

const PlanSelectionStep = ({ onNext }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUser, user } = useSelector(state => state?.auth || {});
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email') || currentUser?.email || user?.email || '';
  const paymentComplete = queryParams.get('paymentComplete');

  const [selectedPlan, setSelectedPlan] = useState("");
  const [subscribeUserLoading, setSubscribeUserLoading] = useState(false);


  const handleSelectPlan = (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan?.comingSoon) {
      setSelectedPlan(planId);
    }
  };

  const debouncedSubscribe = useCallback(
    debounce(async (planId) => {
      try {
        setSubscribeUserLoading(true);
        const res = await axios.post(`${process.env.OPULENT_NODE_API_URL}/payment/subscribe`, {
          planId: 1,
          onBoarding: true
        });

        const checkoutUrl = res.data.checkoutUrl;
        window.location.href = checkoutUrl;
      } catch (error) {
        console.error('Error creating checkout session:', error);
      } finally {
        setSubscribeUserLoading(false);
      }
    }, 500, { leading: true, trailing: false }),
    []
  );

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await axios.get(
          `${process.env.OPULENT_NODE_API_URL}/payment/current`
        );
        
        if(['active', 'trialing'].includes(response?.data?.data?.status)){
          // Store completion data for other tabs
          const completionData = {
            timestamp: Date.now().toString(),
            email: email,
            planId: selectedPlan,
            subscriptionActive: true
          };
          localStorage.setItem("planSelectionCompleted", JSON.stringify(completionData));
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ "currentStep": 4, "maxStep": 4 }));
          dispatch(setField({
            module: 'currentUser',
            field: 'sw_payment_flag',
            value: true
          }));
          navigate(location.pathname, { replace: true });
          onNext()
        }
      } catch (err) {
        console.error("Failed to fetch subscription:", err);
      }
    };

    if (email && paymentComplete) {
      fetchSubscription()
    }
  }, [email, paymentComplete, onNext, selectedPlan]);

  // Listen for completion from other tabs
  useEffect(() => {
    const handleStorageEvent = (e) => {
      if (e.key === "planSelectionCompleted" && e.newValue) {
        try {
          const completionData = JSON.parse(e.newValue);
          if (completionData.subscriptionActive) {
            localStorage.removeItem("planSelectionCompleted");
            onNext();
          }
        } catch (error) {
          console.error("Error parsing completion data:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageEvent);
    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [onNext]);

  return (
    <div className="plan-selection-container">
      <div className="header animate-fade-in">
        <h2 className="inner-title">
          Choose Your Plan
        </h2>
        <p className="subtitle">
          Select the perfect plan for your business needs. You can upgrade or downgrade anytime.
        </p>
      </div>

      <div className="plan-grid">
        {plans.map((plan) => (
          <div
            key={plan.id}
            onClick={() => handleSelectPlan(plan.id)}
            className={`plan-card ${selectedPlan === plan.id && !plan.comingSoon ? 'selected' : ''} ${plan.comingSoon ? 'coming-soon' : ''} ${plan.recommended && !plan.comingSoon ? 'recommended' : ''}`}
          >
            {plan.comingSoon && (
              <div className="badge coming-soon">
                Coming Soon
              </div>
            )}
            {plan.popular && !plan.comingSoon && (
              <div className="badge popular">
                <Star className="w-4 h-4" />
                Most Popular
              </div>
            )}

            <div className="plan-header">
              <h3 className="plan-name">
                {plan.name}
              </h3>
              <div className="plan-price">
                <span className="plan-price-amount">
                  ${plan.price}
                </span>
                <span className="plan-price-period">/{plan.period}</span>
              </div>
              <p className="plan-description">
                {plan.description}
              </p>
            </div>

            <ul className="features">
              {plan.features.map((feature, index) => (
                <li key={index} className="feature">
                  <span className="feature-icon">
                  <Check className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                  </span>
                  <span className="feature-text">{feature}</span>
                </li>
              ))}
            </ul>

            <div className="d-flex justify-content-center">
              <button
                className="btn-primary w-100"
                onClick={() => handleSelectPlan(plan.id)}
                disabled={plan.comingSoon}
              >
                {plan.comingSoon
                  ? "Coming Soon"
                  : selectedPlan === plan.id
                    ? "Selected"
                    : "Select Plan"
                }
              </button>
            </div>

                {selectedPlan === plan.id && (
                  <div className="selected-indicator">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
          </div>
        ))}
      </div>

      <div className="actions-center">
        <button
          onClick={() => debouncedSubscribe(selectedPlan)}
          className="btn-primary"
          style={{minWidth: '128px'}}
          disabled={!selectedPlan || subscribeUserLoading }
        >
          {subscribeUserLoading ? (
            <Spin size="small" style={{ marginRight: 8, color: "white" }} />
          ) : null}
          {subscribeUserLoading ? "Processing..." : "Continue"}
        </button>
      </div>
    </div>
  );
};

export { PlanSelectionStep };
